import { RefObject, useEffect } from "react";
import type { ListImperativeAPI } from "react-window";

/** Frames to wait for react-window to grow its spacer back before giving up. */
const MAX_RESTORE_FRAMES = 30;

/**
 * Restores the list's scroll position after an ancestor toggles
 * `display: none` (appointment tabs stay mounted that way).
 *
 * While hidden, react-window sees a 0px container, re-reads scrollTop as 0 and
 * rebuilds its bounds cache from the first few rows only; its spacer becomes
 * avg(those rows) × rowCount. On show, the browser restores the saved offset
 * clamped to that shorter spacer, so the list lands several entries early.
 * Once the spacer has regrown, put back the last offset seen while visible —
 * unless something else (e.g. a scroll-to-item command) has moved the list
 * since the clamp.
 *
 * `containerRef` is the always-mounted wrapper around the (conditionally
 * rendered) List; scroll events are caught in the capture phase there.
 */
export const useRestoreScrollAfterHide = (
  containerRef: RefObject<HTMLElement | null>,
  listRef: RefObject<ListImperativeAPI | null>,
) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let lastVisibleScrollTop = 0;
    let wasHidden = false;
    let rafId: number | null = null;

    const handleScroll = (event: Event) => {
      const list = listRef.current?.element;
      if (!list || event.target !== list || wasHidden || rafId !== null) return;
      if (list.clientHeight > 0) lastVisibleScrollTop = list.scrollTop;
    };

    const restore = (target: number) => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      const list = listRef.current?.element;
      if (!list) return;
      const clampedTop = list.scrollTop;
      let frames = 0;
      const tick = () => {
        rafId = null;
        // Moved by someone else since the clamp: theirs wins.
        if (list.scrollTop !== clampedTop) return;
        if (list.scrollHeight - list.clientHeight >= target) {
          list.scrollTop = target;
          return;
        }
        if (++frames < MAX_RESTORE_FRAMES) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };

    const observer = new ResizeObserver(([entry]) => {
      const hidden = entry.contentRect.height === 0;
      if (wasHidden && !hidden && lastVisibleScrollTop > 0) {
        restore(lastVisibleScrollTop);
      }
      wasHidden = hidden;
    });

    container.addEventListener("scroll", handleScroll, {
      capture: true,
      passive: true,
    });
    observer.observe(container);
    return () => {
      container.removeEventListener("scroll", handleScroll, { capture: true });
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [containerRef, listRef]);
};
